import "#faro.ts"
import "#store/appearance.ts"

import { QueryClientProvider } from "@tanstack/react-query"
import { createRouter, RouterProvider } from "@tanstack/react-router"
import { LucideProvider } from "lucide-react"
import { StrictMode, Suspense } from "react"
import { createRoot } from "react-dom/client"
import CacheProvider from "react-inlinesvg/provider"

import { ThemeProvider, Toaster, TooltipProvider } from "@atlant/ui"

import { queryClient } from "#/providers/query-client.ts"
import { ErrorBoundary } from "#components/errors/error-boundary.tsx"
import { ErrorFallback } from "#components/errors/error-fallback.tsx"
import { Loader } from "#components/loader.tsx"
import { faro } from "#faro.ts"
import { useUserInfo } from "#store/user-info.ts"

import { routeTree } from "./routeTree.gen"

import "./index.css"

const root = createRoot(document.getElementById("root")!)

root.render(
  <div className="flex h-screen items-center justify-center">
    <Loader className="text-primary size-12 animate-[spin_1.2s_linear_infinite]" />
  </div>,
)

useUserInfo
  .getState()
  .fetchUserInfo()
  .then((userInfo) => {
    const router = createRouter({
      routeTree,
      context: { userInfo, queryClient },
      defaultPreload: "intent",
      defaultPendingComponent: () => (
        <div className="flex h-full w-full items-center justify-center">
          <Loader className="text-primary size-8 animate-[spin_1.2s_linear_infinite]" />
        </div>
      ),
      defaultPendingMs: 200,
      defaultPendingMinMs: 300,
    })
    // a workspace rename (here or by a colleague over SSE) refetches me; re-run beforeLoad to move to the new slug
    useUserInfo.subscribe((next, prev) => {
      if (next.state === "Init") return
      router.update({ ...router.options, context: { userInfo: next, queryClient } })
      const slug = (s: typeof next | typeof prev) =>
        s.state === "LoggedIn" ? s.me.organization.slug : null
      if (next.state !== prev.state || slug(next) !== slug(prev)) void router.invalidate()
    })
    root.render(
      <StrictMode>
        <CacheProvider>
          <QueryClientProvider client={queryClient}>
            <Suspense>
              <ErrorBoundary fallback={<ErrorFallback />}>
                <TooltipProvider>
                  <ThemeProvider
                    attribute="class"
                    defaultTheme="light"
                    disableTransitionOnChange
                  >
                    <LucideProvider>
                      <Toaster richColors expand visibleToasts={5} gap={10} />
                      <RouterProvider router={router} />
                    </LucideProvider>
                  </ThemeProvider>
                </TooltipProvider>
              </ErrorBoundary>
            </Suspense>
          </QueryClientProvider>
        </CacheProvider>
      </StrictMode>,
    )
  })
  .catch((err) => {
    faro?.api.pushError(err instanceof Error ? err : new Error(String(err)))
    console.error("App failed to load:", err)
    root.render(
      <StrictMode>
        <ErrorFallback />
      </StrictMode>,
    )
  })
